import {
  estAdministrateurGlobal,
  peutConsulterInstallation,
  peutPlanifierInstallation,
} from '../../Services/droits-installations';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Authentification } from '../../Services/authentification';
import {
  InterventionEditable,
  ContexteIntervention,
  NatureIntervention,
  peutModifierBon,
} from '../../Services/intervention-edition';
import { InterventionActions } from '../intervention-actions/intervention-actions';
import {
  InterventionDialog,
  InterventionDialogData,
} from '../intervention-dialog/intervention-dialog';

type OptionsOuvertureIntervention = {
  transformation?: boolean;
  preselection?: Pick<ContexteIntervention, 'date' | 'categorie'>;
  /** Un menu disparaît à l’ouverture : restituer le focus au jour qui l’a déclenché. */
  retourFocus?: HTMLElement;
};

/** Point d’entrée partagé par la barre supérieure, le calendrier, les tickets et les cartes. */
@Component({
  selector: 'app-intervention-acces',
  imports: [InterventionActions],
  template: `
    @if (edition(); as intervention) {
      @if (peutModifier()) {
        <button
          type="button"
          class="modifier"
          [attr.aria-label]="
            intervention.nature === 'bon' ? 'Modifier le bon' : 'Modifier la planification'
          "
          (click)="ouvrirEdition($event)"
          (keydown)="$event.stopPropagation()"
        >
          <span class="material-symbols-outlined" aria-hidden="true">edit</span>
          {{ intervention.nature === 'bon' ? 'Modifier' : 'Modifier la planification' }}
        </button>
        @if (peutTransformer()) {
          <button
            type="button"
            class="modifier"
            (click)="transformer($event)"
            (keydown)="$event.stopPropagation()"
          >
            <span class="material-symbols-outlined" aria-hidden="true">assignment_turned_in</span>
            Transformer en bon
          </button>
        }
      }
    } @else {
      <app-intervention-actions
        [utilisateur]="utilisateur()"
        [droits]="droits()"
        [afficherLibelle]="afficherLibelle()"
        [compteur]="compteur()"
        [calendrier]="calendrier() && peutConsulter()"
        [libelle]="libelle()"
        [texteBouton]="texteBouton()"
        [infobulle]="infobulle()"
        (choisir)="ouvrir($event)"
      />
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    .modifier {
      min-height: 40px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: 1px solid var(--line);
      border-radius: 8px;
      color: #075546;
      background: white;
      padding: 8px 12px;
      font: inherit;
      cursor: pointer;
    }
    .modifier:hover {
      background: var(--sidebar-light);
      border-color: var(--green-deep);
    }
    .modifier .material-symbols-outlined {
      font-size: 18px;
    }
    .modifier:focus-visible {
      outline: 3px solid #15334b;
      outline-offset: 3px;
    }
  `,
})
export class InterventionAcces {
  private readonly auth = inject(Authentification);
  private readonly dialog = inject(MatDialog);
  readonly ticketId = input<string | number | null>(null);
  readonly installationId = input<number | null>(null);
  /** Liste déjà chargée du parc, pour les points d’entrée sans centrale imposée. */
  readonly installationsDisponibles = input<readonly { id: number }[] | null>(null);
  readonly edition = input<InterventionEditable | null>(null);
  /** Permet à une consultation déjà ouverte de suspendre son piège de focus pendant l’édition. */
  readonly ouverture = output<MatDialogRef<InterventionDialog>>();
  readonly afficherLibelle = input(true);
  readonly calendrier = input(false);
  readonly compteur = input<number | null>(null);
  readonly libelle = input('Actions sur les interventions');
  readonly texteBouton = input('Interventions');
  readonly infobulle = input('');
  readonly utilisateur = this.auth.tokenInfoSignal;
  readonly peutConsulter = computed(() => peutConsulterInstallation(this.utilisateur()));
  /** Une édition utilise son rattachement propre, jamais celui d’une autre page ouverte. */
  readonly droits = computed(() => {
    const edition = this.edition();
    const id = edition ? Number(edition.valeurs.installationId) : this.installationId();
    // Un ticket sans centrale connue ne doit pas hériter d’un rôle détenu ailleurs.
    const disponibles = this.installationsDisponibles();
    const planifier =
      id === null && !this.ticketId() && disponibles !== null
        ? disponibles.some((i) => peutPlanifierInstallation(this.utilisateur(), i.id))
        : peutPlanifierInstallation(
            this.utilisateur(),
            id ?? (this.ticketId() && !estAdministrateurGlobal(this.utilisateur()) ? 0 : undefined),
          );
    return { planifier, modifierPlanification: planifier };
  });
  /** Le créateur ou un administrateur peut modifier un bon ; les planifications suivent leurs droits. */
  readonly peutModifier = computed(() => {
    const edition = this.edition();
    return edition?.nature === 'bon'
      ? peutModifierBon(
          this.utilisateur(),
          edition.auteurId,
          Number(edition.valeurs.installationId),
        )
      : !!edition && this.droits().modifierPlanification;
  });
  /** Les routes de bons existantes portent sur les interventions curatives rattachées à un ticket. */
  readonly peutTransformer = computed(() => {
    const edition = this.edition();
    return (
      this.peutModifier() &&
      edition?.nature === 'planification' &&
      edition.valeurs.categorie === 'curative' &&
      Number(edition.valeurs.ticketId) > 0
    );
  });

  /** Complète la même intervention via l’édition du bon ; aucune création en parallèle. */
  transformer(event: Event): void {
    event.stopPropagation();
    if (this.peutTransformer()) {
      this.focaliserDeclencheur(event);
      this.ouvrir('bon', { transformation: true });
    }
  }

  /** Ouvre l’édition sans déclencher le clic de la carte qui contient le bouton. */
  ouvrirEdition(event: Event): void {
    event.stopPropagation();
    const edition = this.edition();
    if (edition && this.peutModifier()) {
      this.focaliserDeclencheur(event);
      this.ouvrir(edition.nature);
    }
  }

  /** Safari ne focalise pas un bouton cliqué ; les dialogues doivent mémoriser le bon déclencheur. */
  private focaliserDeclencheur(event: Event): void {
    if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
  }

  /** Vérifie les droits d’interface et ouvre une unique modale avec le contexte du point d’entrée. */
  ouvrir(nature: NatureIntervention, options: OptionsOuvertureIntervention = {}): void {
    const transformation = options.transformation ?? false;
    if (!this.utilisateur() || this.dialog.getDialogById('intervention-edition')) return;
    if (
      this.edition() ? !this.peutModifier() : nature === 'planification' && !this.droits().planifier
    )
      return;
    if (transformation && !this.peutTransformer()) return;
    const data: InterventionDialogData = {
      nature,
      ticketId: this.ticketId(),
      installationId: this.installationId(),
      edition: this.edition(),
      ...(!this.edition() && nature === 'planification' && options.preselection
        ? { preselection: options.preselection }
        : {}),
      ...(transformation ? { transformation: true } : {}),
    };
    const reference = this.dialog.open(InterventionDialog, {
      id: 'intervention-edition',
      panelClass: 'intervention-dialog-panel',
      data,
      width: '620px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      ariaLabel: transformation
        ? 'Transformer en bon d’intervention'
        : this.edition()
          ? 'Modifier une intervention'
          : nature === 'bon'
            ? 'Créer un bon d’intervention'
            : 'Planifier une intervention',
      // Le conteneur reçoit d’abord le focus ; le titre prendra le relais après le chargement.
      autoFocus: 'dialog',
      restoreFocus: options.retourFocus ?? true,
    });
    this.ouverture.emit(reference);
  }
}
